import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronRight, FaRocket } from "react-icons/fa";
import GradientHouseIcon from "../components/GradientHouseIcon";
import VendorleadsGradientText from "../components/VendorleadsGradientText";
import SignInModal from "../components/SignInModal";
import { trackAgentEvent } from "../utils/agentEventTracking";

const BRAND_COLOR = "#1a3f6b";
const BRAND_ACCENT = "#10b981";

const FEATURES = [
  {
    emoji: "📊",
    badge: "MONTHLY REPORTS",
    title: "AI-Powered Monthly Property Reports",
    description: "Automatically send beautifully branded, data-rich property reports every month. AI analyses comparable sales, estimates values, and keeps homeowners engaged.",
    image: "https://realestate-reports-assets.s3.us-west-2.amazonaws.com/vendorleads-demo/property_update_email_preview.png",
  },
  {
    emoji: "🏠",
    badge: "DIY VALUATOR",
    title: "Self-Service AI Property Valuator",
    description: "Give homeowners an instant, interactive property report by searching any address on your branded landing page with AI-powered analysis.",
    image: "https://realestate-reports-assets.s3.us-west-2.amazonaws.com/product_updates/20260401/property_analysis_results.png",
  },
  {
    emoji: "📰",
    badge: "AI ARTICLES",
    title: "Data-Driven AI News Articles",
    description: "Publish professional, AI-written real estate articles covering sales, rentals, suburb profiles and more — all completely agency branded.",
    image: "https://realestate-reports-assets.s3.us-west-2.amazonaws.com/vendorleads-demo/feature_news_article/news_article.png",
  },
  {
    emoji: "✉️",
    badge: "EMAIL CAMPAIGNS",
    title: "AI Email Campaign Builder",
    description: "Build and broadcast beautiful email campaigns. Select AI articles, pull in live listings, edit with AI prompts, then send to your entire database.",
    image: "https://realestate-reports-assets.s3.us-west-2.amazonaws.com/product_updates/20260401/ai_email_campaign.png",
  },
  {
    emoji: "💬",
    badge: "SMS — PROPERTY UPDATE",
    title: "Personalised Property SMS Campaigns",
    description: "Send address-matched property insights straight to contacts' phones. Each SMS links to a live AI property report for their home.",
    image: null,
    smsBubbles: [
      { type: "incoming", text: "John at Demo Agency is now offering a free AI property value tracker. Here's an example of what you get each month — price estimates, recent sales and property activity around 22 Sevira Ave, Gledswood Hills.\n\nSee your report: homeprice.site/p/22-sevira-ave..." },
      { type: "outgoing", text: "Thanks John! The report looks great 👍" },
      { type: "incoming", text: "Glad you found it useful! Let me know if you'd like a more detailed appraisal — happy to help anytime." },
    ],
  },
  {
    emoji: "️",
    badge: "MAP PROSPECTOR",
    title: "Visualise Contact Activity on a Map",
    description: "See every contact interaction plotted on an interactive map — property analyses, email opens, SMS clicks, and report downloads.",
    image: "https://realestate-reports-assets.s3.us-west-2.amazonaws.com/product_updates/20260401/map_prospector_desktop.png",
  },
  {
    emoji: "🏡",
    badge: "AI APPRAISALS",
    title: "Premium AI Property Appraisals",
    description: "Build stunning, agency-branded property appraisals powered by AI. Score comparables, analyse images, pull suburb data, and publish.",
    image: "https://realestate-reports-assets.s3.us-west-2.amazonaws.com/vendorleads-demo/appraisal_overlay.png",
  },
  {
    emoji: "📱",
    badge: "QR CAMPAIGNS",
    title: "QR Code Print Campaigns",
    description: "Generate branded QR code flyers. Homeowners scan, get an instant property report, and become a captured lead — all on autopilot.",
    image: "https://realestate-reports-assets.s3.us-west-2.amazonaws.com/vendorleads-demo/qr_code_flyer.png",
  },
];

export default function ActivateTrial() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Capture the long `?token=` and the short `?t=` codes on first render.
  // The global TokenIngest in App.js strips both from the URL bar after
  // resolving them, so we read once on mount and stash for the effect below.
  const [initialParams] = useState(() => ({
    token: searchParams.get("token") || "",
    shortCode: searchParams.get("t") || "",
  }));

  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showSignIn, setShowSignIn] = useState(false);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  // Fire once when the visitor scrolls within ~40px of the page bottom. Used
  // to gauge how many activation visitors actually read the full pitch.
  useEffect(() => {
    let fired = false;
    const onScroll = () => {
      if (fired) return;
      const scrollY = window.scrollY || window.pageYOffset || 0;
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      const fullHeight = document.documentElement.scrollHeight;
      if (scrollY + viewport >= fullHeight - 40) {
        fired = true;
        trackAgentEvent({
          eventName: "marketing.activate.page.scroll_bottom",
          metadata: { path: window.location.pathname },
        });
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const host =
      window.location.hostname === "localhost" || window.location.hostname.includes("ngrok")
        ? "https://property-app-dev--production-api-dev.modal.run"
        : "https://property-app--production-api.modal.run";

    let cancelled = false;

    const fetchInfo = (resolvedToken) => {
      if (!resolvedToken) {
        if (!cancelled) setLoading(false);
        return;
      }
      fetch(`${host}/activate-trial-info`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: resolvedToken }),
      })
        .then((r) => r.json())
        .then((data) => {
          if (cancelled) return;
          if (data.status === "success") setAgent(data);
        })
        .catch(() => {})
        .finally(() => { if (!cancelled) setLoading(false); });
    };

    // Prefer the long token if present; otherwise resolve the short ?t= code
    // via /resolve-sms-token. Fall back to any param_token TokenIngest may
    // have already stashed in localStorage (e.g. when the user arrived from
    // a different page first).
    if (initialParams.token) {
      fetchInfo(initialParams.token);
    } else if (initialParams.shortCode) {
      fetch(`${host}/resolve-sms-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: initialParams.shortCode }),
      })
        .then((r) => r.json())
        .then((data) => fetchInfo(data && data.token ? data.token : ""))
        .catch(() => { if (!cancelled) setLoading(false); });
    } else {
      let stashed = "";
      try { stashed = localStorage.getItem("param_token") || ""; } catch (_) { /* ignore */ }
      fetchInfo(stashed);
    }

    return () => { cancelled = true; };
  }, [initialParams]);

  // Preload all carousel images on mount
  useEffect(() => {
    FEATURES.forEach((f) => {
      if (f.image) {
        const img = new window.Image();
        img.src = f.image;
      }
    });
  }, []);

  const goNext = useCallback(() => setCarouselIdx((i) => (i + 1) % FEATURES.length), []);
  const goPrev = useCallback(() => setCarouselIdx((i) => (i - 1 + FEATURES.length) % FEATURES.length), []);

  const handleActivate = () => {
    sessionStorage.setItem("redirect_after_login", "/getting-started");
    setShowSignIn(true);
  };

  if (loading) {
    return (
      <div style={{ display: "flex", height: "100vh", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #134e4a 100%)" }}>
        <div style={{ width: 36, height: 36, border: "3px solid rgba(255,255,255,0.2)", borderTopColor: BRAND_ACCENT, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const feature = FEATURES[carouselIdx];
  const firstName = agent?.agent_first_name || agent?.agent_name?.split(" ")[0] || "";
  const colour = agent?.agent_colour || BRAND_ACCENT;

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #134e4a 100%)", position: "relative", overflow: "hidden" }}>
      {/* Subtle grid overlay */}
      <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "32px 32px", pointerEvents: "none" }} />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 960, margin: "0 auto", padding: isMobile ? "40px 20px" : "60px 32px" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: isMobile ? 32 : 48 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 20 }}>
            <GradientHouseIcon width={isMobile ? 28 : 34} height={isMobile ? 28 : 34} />
            <VendorleadsGradientText style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800 }} />
          </div>

          {/* Personalised greeting */}
          <h1 style={{ fontSize: isMobile ? 26 : 38, fontWeight: 800, color: "#fff", margin: "0 0 12px", letterSpacing: "-0.02em", lineHeight: 1.2 }}>
            {firstName ? `Welcome, ${firstName}! 🎉` : "Welcome! 🎉"}
          </h1>
          <p style={{ fontSize: isMobile ? 16 : 19, color: "rgba(255,255,255,0.7)", margin: "0 0 8px", lineHeight: 1.6, maxWidth: 580, marginLeft: "auto", marginRight: "auto" }}>
            Your account is already set up and free forever on the Starter plan. Log in to start sending AI-powered property updates, reports, and campaigns to your contacts.
          </p>

          {/* Included in trial */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", marginTop: 20 }}>
            {[
              { icon: "🔓", label: "All features unlocked" },
            ].map((item, i) => (
              <div key={i} style={{
                background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 10, padding: "8px 16px", display: "flex", alignItems: "center", gap: 8,
                fontSize: 14, color: "rgba(255,255,255,0.85)", fontWeight: 500,
              }}>
                <span style={{ fontSize: 16 }}>{item.icon}</span> {item.label}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div style={{ textAlign: "center", marginBottom: isMobile ? 28 : 40 }}>
          <button
            onClick={handleActivate}
            style={{
              display: "inline-flex", alignItems: "center", gap: 10,
              padding: isMobile ? "14px 36px" : "16px 48px", borderRadius: 12,
              background: `linear-gradient(135deg, ${BRAND_ACCENT} 0%, #3b82f6 100%)`,
              color: "#fff", fontSize: isMobile ? 16 : 18, fontWeight: 700,
              border: "none", cursor: "pointer",
              boxShadow: `0 4px 20px rgba(16,185,129,0.4)`,
              transition: "transform 0.15s, box-shadow 0.15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 30px rgba(16,185,129,0.5)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(16,185,129,0.4)"; }}
          >
            <FaRocket /> Log In & Explore
          </button>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", marginTop: 12 }}>
            No credit card needed. Your account is free &mdash; no trial, no expiry.
          </p>
        </div>

        {/* Branded property update preview */}
        {agent && (
          <div style={{
            background: "rgba(255,255,255,0.06)", borderRadius: 16,
            border: "1px solid rgba(255,255,255,0.1)", overflow: "hidden",
            marginBottom: isMobile ? 28 : 40, padding: isMobile ? "20px 16px" : "24px 28px",
          }}>
            <div style={{ marginBottom: 12 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: BRAND_ACCENT, textTransform: "uppercase", letterSpacing: "0.08em" }}>Preview</span>
              <p style={{ margin: "4px 0 0", fontSize: 14, fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>
                📨 Here's what your contacts receive each month
              </p>
            </div>
            <div style={{
              borderRadius: 8, overflow: "hidden", border: "1px solid rgba(255,255,255,0.15)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            }}>
              {/* Email header */}
              <div style={{ background: colour, padding: "10px 12px", display: "flex", alignItems: "center", gap: 8 }}>
                {agent.agent_profile_image && (
                  <img src={agent.agent_profile_image} alt="" style={{ width: 30, height: 30, borderRadius: "50%", objectFit: "cover" }} />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{agent.agent_name || "Your Name"}</div>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.75)" }}>{agent.agency_name || "Your Agency"}</div>
                </div>
                {agent.logo_url && (
                  <img src={agent.logo_url} alt="" style={{ height: 28, maxWidth: 60, objectFit: "contain" }} />
                )}
              </div>
              {/* Email body */}
              <div style={{ background: "#fff", padding: "14px 12px" }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>Monthly Property Update</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#1a1a1a", lineHeight: 1.3, marginBottom: 10 }}>22 Sevira Ave, Gledswood Hills NSW 2557</div>
                <div style={{ background: "#f0fdfa", borderRadius: 6, padding: "8px 10px" }}>
                  <span style={{ fontSize: 11, color: "#134e4a" }}>📈 Est. <strong>$1.29M</strong> (+4.2%) · <strong>3 recent sales</strong> nearby · Suburb median up <strong>3.8%</strong></span>
                </div>
                <div style={{ textAlign: "center", marginTop: 12 }}>
                  <a
                    href={`https://www.homeprice.site/property-profile/22-sevira-avenue-gledswood-hills-nsw-2557-vl3272ep${agent.landing_page_slug ? `?agent=${agent.landing_page_slug}` : ""}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-block", padding: "8px 20px",
                      backgroundColor: colour, color: "#fff", borderRadius: 6,
                      textDecoration: "none", fontWeight: 700, fontSize: 11,
                    }}
                  >
                    View Full Property Report →
                  </a>
                </div>
              </div>
              {/* Email footer */}
              <div style={{ background: "#f9fafb", borderTop: "1px solid #f3f4f6", padding: "8px 12px", textAlign: "center" }}>
                <span style={{ fontSize: 10, color: "#9ca3af" }}>Sent on behalf of {agent.agent_name || "you"}</span>
              </div>
            </div>
            <p style={{ margin: "10px 0 0", fontSize: 12, color: "rgba(255,255,255,0.4)", textAlign: "center", fontStyle: "italic" }}>
              Preview only — actual reports include live data, comparable sales & AI insights.
            </p>
          </div>
        )}

        {/* Feature carousel */}
        <div style={{
          background: "rgba(255,255,255,0.06)", borderRadius: 20,
          border: "1px solid rgba(255,255,255,0.1)", overflow: "hidden",
          marginBottom: isMobile ? 28 : 40,
        }}>
          {/* Carousel content */}
          <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", height: isMobile ? "auto" : 400 }}>
            {/* Image / SMS side */}
            <div style={{
              flex: isMobile ? "none" : "1 1 50%",
              background: "rgba(0,0,0,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: isMobile ? 20 : 24,
              position: "relative",
            }}>
              {feature.smsBubbles ? (
                <div key={carouselIdx} style={{ width: "100%", maxWidth: 300, animation: "fadeIn 0.4s ease" }}>
                  {feature.smsBubbles.map((bubble, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: bubble.type === "incoming" ? "flex-start" : "flex-end", marginBottom: 6 }}>
                      <div style={{
                        maxWidth: "85%",
                        background: bubble.type === "incoming" ? "#e5e5ea" : "#007aff",
                        color: bubble.type === "incoming" ? "#000" : "#fff",
                        borderRadius: bubble.type === "incoming" ? "14px 14px 14px 4px" : "14px 14px 4px 14px",
                        padding: "6px 10px", fontSize: 11, lineHeight: 1.4, whiteSpace: "pre-wrap",
                      }}>
                        {bubble.text}
                      </div>
                    </div>
                  ))}
                </div>
              ) : feature.image ? (
                <img
                  key={carouselIdx}
                  src={feature.image} alt={feature.title}
                  style={{
                    maxWidth: "100%", maxHeight: "100%", borderRadius: 10,
                    boxShadow: "0 4px 24px rgba(0,0,0,0.3)",
                    animation: "fadeIn 0.4s ease", objectFit: "contain",
                  }}
                />
              ) : (
                <div style={{
                  width: 120, height: 120, borderRadius: "50%",
                  background: `linear-gradient(135deg, ${colour}, ${BRAND_ACCENT})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 52, animation: "fadeIn 0.4s ease",
                }}>
                  {feature.emoji}
                </div>
              )}
            </div>

            {/* Text side */}
            <div style={{
              flex: isMobile ? "none" : "1 1 50%", padding: isMobile ? "24px 20px" : "32px 28px",
              display: "flex", flexDirection: "column", justifyContent: "center",
            }}>
              <div style={{
                display: "inline-block", background: `linear-gradient(90deg, ${BRAND_ACCENT}, #3b82f6)`,
                color: "#fff", fontSize: 11, fontWeight: 700, letterSpacing: "0.05em",
                padding: "4px 10px", borderRadius: 6, marginBottom: 12, width: "fit-content",
              }}>
                {feature.badge}
              </div>
              <h3 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: "#fff", margin: "0 0 10px", lineHeight: 1.3 }}>
                {feature.title}
              </h3>
              <p style={{ fontSize: 14, color: "rgba(255,255,255,0.65)", lineHeight: 1.6, margin: 0 }}>
                {feature.description}
              </p>
            </div>
          </div>

          {/* Carousel controls */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 16, padding: "12px 20px", borderTop: "1px solid rgba(255,255,255,0.08)",
          }}>
            <button onClick={goPrev} style={navBtn}><FaChevronLeft size={12} /></button>
            <div style={{ display: "flex", gap: 6 }}>
              {FEATURES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCarouselIdx(i)}
                  style={{
                    width: carouselIdx === i ? 20 : 8, height: 8, borderRadius: 4,
                    background: carouselIdx === i ? BRAND_ACCENT : "rgba(255,255,255,0.2)",
                    border: "none", cursor: "pointer", transition: "all 0.3s", padding: 0,
                  }}
                />
              ))}
            </div>
            <button onClick={goNext} style={navBtn}><FaChevronRight size={12} /></button>
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: "center", marginTop: isMobile ? 40 : 60, paddingBottom: 20 }}>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.3)" }}>
            © 2026 Vendorleads. All rights reserved.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {showSignIn && <SignInModal show={showSignIn} onHide={() => setShowSignIn(false)} initialStep="email" initialEmail={agent?.email || ''} />}
    </div>
  );
}

const navBtn = {
  width: 32, height: 32, borderRadius: "50%",
  background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)",
  color: "rgba(255,255,255,0.6)", cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  transition: "background 0.15s",
};
